import { useId, useState } from 'react'
import { money, moneyShort } from '../lib/money'

/**
 * Ba hình vẽ số liệu cho màn hình báo cáo.
 *
 * Cố ý không kéo thư viện đồ thị nào về: máy quầy chạy trên điện thoại Android rẻ, app
 * phải mở được lúc mất mạng, và ba hình dưới đây vẽ bằng SVG với chưa tới 200 dòng.
 *
 * Vài luật đi theo từng hình, không phải để cho đẹp mà để đọc không sai:
 *
 *   • Một hình một trục. Không bao giờ vẽ doanh thu và số đơn chồng lên nhau bằng hai
 *     thang đo — đó là cách nhanh nhất để hai đường cắt nhau ở một chỗ vô nghĩa và người
 *     đọc tưởng có chuyện gì xảy ra ở đó. Số đơn nằm trong ô chú thích khi rê chuột.
 *   • Cùng một dãy số thì một màu, đậm nhạt theo giá trị. Nhiều màu chỉ dùng khi các lát
 *     là những thứ KHÁC NHAU (nhóm món, cách trả tiền).
 *   • Mỗi lát đều có tên và số viết bằng chữ. Không bao giờ để màu là thứ duy nhất
 *     phân biệt hai lát — khoảng 1 trong 12 đàn ông không phân biệt được đỏ với xanh lá.
 */

// ── ô chú thích khi rê chuột ──

interface TipState {
  x: number
  y: number
  title: string
  lines: string[]
}

function Tip({ tip }: { tip: TipState | null }) {
  if (!tip) return null
  return (
    <div className="chart-tip" style={{ left: `${tip.x}%`, top: `${tip.y}%` }} role="status">
      <b>{tip.title}</b>
      {tip.lines.map((l) => (
        <span key={l}>{l}</span>
      ))}
    </div>
  )
}

// ── cột theo thời gian ──

export interface TrendPoint {
  label: string
  revenue: number
  orderCount: number
}

/**
 * Doanh thu theo mốc thời gian.
 *
 * Một dãy số duy nhất nên dùng một màu, và không cần bảng chú thích — cái tiêu đề đã nói
 * đây là doanh thu rồi. Cột nào cao nhất thì đậm nhất, để mắt bắt ngay giờ đông khách.
 */
export function BarTrend({ points }: { points: TrendPoint[] }) {
  const [tip, setTip] = useState<TipState | null>(null)
  const max = Math.max(...points.map((p) => p.revenue), 1)

  // Chỉ ghi nhãn thưa ra khi có nhiều cột, không thì chữ chồng lên nhau.
  const step = points.length > 14 ? Math.ceil(points.length / 8) : 1

  return (
    <div className="chart">
      <div className="chart-grid" aria-hidden="true">
        {[1, 0.5, 0].map((f) => (
          <div key={f} className="chart-line">
            <span>{f === 0 ? '0' : moneyShort(max * f)}</span>
          </div>
        ))}
      </div>

      <div className="chart-bars" onPointerLeave={() => setTip(null)}>
        {points.map((p, i) => (
          <div
            key={p.label}
            className="chart-col"
            onPointerEnter={() =>
              setTip({
                x: ((i + 0.5) / points.length) * 100,
                y: 0,
                title: p.label,
                lines: [
                  `Doanh thu ${money(p.revenue)}đ`,
                  p.orderCount > 0 ? `${p.orderCount} đơn` : 'Chưa có đơn nào',
                ],
              })
            }
          >
            <div
              className={`chart-bar${p.revenue === max && max > 0 ? ' is-peak' : ''}`}
              style={{ height: `${(p.revenue / max) * 100}%` }}
            />
            <span className="chart-x">{i % step === 0 ? p.label : ''}</span>
          </div>
        ))}
      </div>

      <Tip tip={tip} />

      {/* Người dùng trình đọc màn hình đọc bảng này, không đọc hình vẽ. */}
      <table className="sr-only">
        <caption>Doanh thu theo thời gian</caption>
        <tbody>
          {points.map((p) => (
            <tr key={p.label}>
              <th scope="row">{p.label}</th>
              <td>{money(p.revenue)} đồng</td>
              <td>{p.orderCount} đơn</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ── phần trăm trên một dải ──

export interface Slice {
  name: string
  revenue: number
  orderCount: number
}

/**
 * Cơ cấu: mỗi lát chiếm bao nhiêu phần của tổng.
 *
 * Một dải ngang chia đoạn, không phải hình tròn. Tên nhóm món tiếng Việt dài ("Trà trái
 * cây", "Chưa xếp nhóm") nên xếp quanh vòng tròn là chữ đè lên nhau; mà so hai lát gần
 * bằng nhau trên vòng tròn thì mắt người vốn đã đoán sai. Dải ngang thì đọc thẳng, và
 * bảng chú thích bên dưới ghi rõ số tiền lẫn phần trăm.
 */
export function ShareBar({ slices, total }: { slices: Slice[]; total: number }) {
  const id = useId()
  const [tip, setTip] = useState<TipState | null>(null)

  if (total <= 0 || slices.length === 0) {
    return <p className="chart-empty">Kỳ này chưa có doanh thu để chia.</p>
  }

  const pct = (v: number) => (v / total) * 100
  const fmt = (v: number) => `${pct(v).toFixed(1).replace('.', ',')}%`

  return (
    <div className="share">
      <div className="share-track" onPointerLeave={() => setTip(null)}>
        {slices.map((s, i) => (
          <div
            key={s.name}
            className={`share-seg tone-${Math.min(i, 5)}`}
            style={{ width: `${pct(s.revenue)}%` }}
            onPointerEnter={() =>
              setTip({ x: 50, y: 0, title: s.name, lines: [`${money(s.revenue)}đ · ${fmt(s.revenue)}`] })
            }
          />
        ))}
        <Tip tip={tip} />
      </div>

      <ul className="share-keys" aria-describedby={id}>
        {slices.map((s, i) => (
          <li key={s.name}>
            <span className={`share-dot tone-${Math.min(i, 5)}`} aria-hidden="true" />
            <span className="share-name">{s.name}</span>
            <span className="share-amount num">{moneyShort(s.revenue)}</span>
            <span className="share-pct num">{fmt(s.revenue)}</span>
          </li>
        ))}
      </ul>
      <span id={id} className="sr-only">
        Tổng {money(total)} đồng
      </span>
    </div>
  )
}

// ── xếp hạng ──

export interface RankRow {
  name: string
  qtyText: string
  revenue: number
}

/** Top món bán chạy. Một dãy số, một màu, dài ngắn theo doanh thu. */
export function RankBars({ rows }: { rows: RankRow[] }) {
  if (rows.length === 0) return <p className="chart-empty">Kỳ này chưa bán được món nào.</p>

  const max = Math.max(...rows.map((r) => r.revenue), 1)
  const total = rows.reduce((s, r) => s + r.revenue, 0)

  return (
    <ol className="rank">
      {rows.map((r, i) => (
        <li key={r.name} className="rank-row">
          <span className="rank-no num">{i + 1}</span>
          <span className="rank-name">{r.name}</span>
          <span className="rank-track" aria-hidden="true">
            <span className="rank-fill" style={{ width: `${(r.revenue / max) * 100}%` }} />
          </span>
          <span className="rank-qty">{r.qtyText}</span>
          <span className="rank-pct num">
            {total > 0 ? `${((r.revenue / total) * 100).toFixed(1).replace('.', ',')}%` : '—'}
          </span>
        </li>
      ))}
    </ol>
  )
}
